import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSupabase } from '../utils/supabase';
import { marked } from 'marked';
import SocialShare from './SocialShare';
import { X } from 'lucide-react';

// Configure marked for better formatting
marked.setOptions({
  gfm: true,
  breaks: true,
  headerIds: true,
  headerPrefix: 'heading-',
});

interface BlogPost {
  id: string;
  title: string;
  content: string;
  created_at: string;
  publish_date: string;
  cover_image?: string;
  cover_image_alt?: string;
  author: string;
  view_count: number;
  tags: string[];
  categories: string[];
  excerpt?: string;
}

interface TagInputProps {
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  maxTags?: number;
}

const TagInput: React.FC<TagInputProps> = ({ tags, onTagsChange, maxTags = 4 }) => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const newTag = inputValue.trim();
      
      if (!newTag) return;
      
      if (tags.includes(newTag)) {
        setError('Tag already exists');
        return;
      }
      
      if (tags.length >= maxTags) {
        setError(`Maximum ${maxTags} tags allowed`);
        return;
      }
      
      onTagsChange([...tags, newTag]);
      setInputValue('');
      setError(null);
    }
  };

  const removeTag = (tagToRemove: string) => {
    onTagsChange(tags.filter(tag => tag !== tagToRemove));
    setError(null);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 mb-2">
        {tags.map(tag => (
          <span 
            key={tag} 
            className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
          >
            {tag}
            <button
              onClick={() => removeTag(tag)}
              className="ml-2 text-blue-600 hover:text-blue-800"
            >
              <X className="h-4 w-4" />
            </button>
          </span>
        ))}
      </div>
      <div>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={tags.length >= maxTags ? `Maximum ${maxTags} tags reached` : "Add tags (press Enter or comma)"}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={tags.length >= maxTags}
        />
        {error && (
          <p className="text-red-600 text-sm mt-1">{error}</p>
        )}
        <p className="text-gray-500 text-sm mt-1">
          {maxTags - tags.length} tags remaining
        </p>
      </div>
    </div>
  );
};

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = React.useState<BlogPost | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const { supabase } = useSupabase();

  React.useEffect(() => {
    async function fetchPost() {
      if (!slug) {
        setLoading(false);
        setError('No slug provided');
        return;
      }

      try {
        if (!supabase) {
          throw new Error('Supabase client not initialized');
        }

        const { data, error: fetchError } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('slug', slug)
          .eq('status', 'published')
          .single();

        if (fetchError) {
          console.error('Error fetching post:', fetchError);
          throw new Error(fetchError.message || 'Failed to fetch post');
        }

        if (!data) {
          setPost(null);
          setError('Post not found');
          setLoading(false);
          return;
        }

        setPost(data);
        
        const { error: updateError } = await supabase
          .from('blog_posts')
          .update({ view_count: (data.view_count || 0) + 1 })
          .eq('id', data.id);

        if (updateError) {
          console.error('Error updating view count:', updateError);
        }
      } catch (err) {
        console.error('Error in fetchPost:', err);
        setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    }

    fetchPost();
  }, [slug, supabase]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse max-w-4xl mx-auto">
          <div className="h-8 bg-gray-200 rounded w-3/4 mb-8"></div>
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-12"></div>
          <div className="space-y-4">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Error</h1>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Post not found</h1>
          <p>The blog post you're looking for doesn't exist or hasn't been published yet.</p>
        </div>
      </div>
    );
  }

  const currentUrl = window.location.href;

  return (
    <div className="container mx-auto px-4 py-12">
      <article className="max-w-4xl mx-auto">
        {post.cover_image && (
          <div className="mb-8 rounded-lg overflow-hidden shadow-lg">
            <img
              src={post.cover_image}
              alt={post.cover_image_alt || `Featured image for article: ${post.title}`}
              className="w-full h-[400px] object-cover"
            />
          </div>
        )}
        
        <header className="mb-12">
          <h1 className="text-5xl font-bold mb-6">{post.title}</h1>
          <div className="flex items-center justify-between text-gray-600 mb-6">
            <div className="flex items-center space-x-4">
              <span className="font-medium">{post.author}</span>
              <time className="text-gray-500">
                {new Date(post.publish_date || post.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </time>
            </div>
            <span className="flex items-center text-sm">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              {post.view_count} views
            </span>
          </div>
          
          <div className="flex flex-wrap gap-2 mb-8">
            {post.tags?.map(tag => (
              <span key={tag} className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                #{tag}
              </span>
            ))}
            {post.categories?.map(category => (
              <span key={category} className="px-3 py-1 bg-indigo-100 text-indigo-800 text-sm rounded-full">
                {category}
              </span>
            ))}
          </div>
          
          {post.excerpt && (
            <p className="text-xl text-gray-600 leading-relaxed">
              {post.excerpt}
            </p>
          )}

          <div className="mt-8">
            <SocialShare
              url={currentUrl}
              title={post.title}
              description={post.excerpt}
              contentId={post.id}
            />
          </div>
        </header>

        <div className="prose prose-lg max-w-none">
          <div
            className="markdown-content"
            dangerouslySetInnerHTML={{ __html: marked(post.content) }}
          />
        </div>

        <footer className="mt-12 pt-8 border-t border-gray-200">
          <SocialShare
            url={currentUrl}
            title={post.title}
            description={post.excerpt}
            contentId={post.id}
          />
        </footer>
      </article>
    </div>
  );
};

export default BlogPost;